import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCreateSupportFAQ, useUpdateSupportFAQ } from '@/hooks/use-support-faqs';
import { useSupportCategories } from '@/hooks/use-support-categories';
import { useEffect } from 'react';

const faqSchema = z.object({
  question: z.string().min(1, 'Pergunta é obrigatória').max(500, 'Pergunta muito longa'),
  answer: z.string().min(10, 'Resposta deve ter pelo menos 10 caracteres'),
  category_id: z.string().optional(),
  is_published: z.boolean(),
  sort_order: z.number().min(0).max(999),
});

type FAQFormData = z.infer<typeof faqSchema>;

interface FAQModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  faq?: any;
}

export function FAQModal({ open, onOpenChange, faq }: FAQModalProps) {
  const { data: categories } = useSupportCategories();
  const createFAQ = useCreateSupportFAQ();
  const updateFAQ = useUpdateSupportFAQ(faq?.id || '');

  const form = useForm<FAQFormData>({
    resolver: zodResolver(faqSchema),
    defaultValues: {
      question: '',
      answer: '',
      is_published: true,
      sort_order: 100,
    },
  });

  useEffect(() => {
    if (faq) {
      form.reset({
        question: faq.question,
        answer: faq.answer,
        category_id: faq.category_id || undefined,
        is_published: faq.is_published,
        sort_order: faq.sort_order,
      });
    } else {
      form.reset({
        question: '',
        answer: '',
        is_published: true,
        sort_order: 100,
      });
    }
  }, [faq, form]);

  const onSubmit = async (data: FAQFormData) => {
    try {
      if (faq) {
        await updateFAQ.mutateAsync({
          question: data.question,
          answer: data.answer,
          category_id: data.category_id,
          is_published: data.is_published,
          sort_order: data.sort_order,
        });
      } else {
        await createFAQ.mutateAsync({
          question: data.question,
          answer: data.answer,
          category_id: data.category_id,
          is_published: data.is_published,
          sort_order: data.sort_order,
        });
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving FAQ:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{faq ? 'Editar FAQ' : 'Criar FAQ'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pergunta</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite a pergunta frequente" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoria</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories?.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Resposta</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Digite a resposta detalhada"
                      className="min-h-[200px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ordem de Exibição</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="999"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_published"
                render={({ field }) => (
                  <FormItem className="flex flex-col justify-end">
                    <FormLabel>Publicado</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={createFAQ.isPending || updateFAQ.isPending}
              >
                {createFAQ.isPending || updateFAQ.isPending 
                  ? 'Salvando...' 
                  : faq ? 'Atualizar' : 'Criar FAQ'
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}